import React, { useMemo, useRef } from "react";

import "./Visa.css";

// Deterministic helpers (stable per-country so stamps don't "shuffle" on rerender)
function hashStringToUint32(input) {
  // FNV-1a 32-bit
  let hash = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function mulberry32(seedUint32) {
  let t = seedUint32 >>> 0;
  return function random() {
    t += 0x6d2b79f5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function formatDate(epochSeconds) {
  const date = new Date(epochSeconds * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function formatUtcTime(epochSeconds) {
  const date = new Date(epochSeconds * 1000);
  return date.toLocaleTimeString("en-US", {
    timeZone: "UTC",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function makeSealToken(input) {
  // Create a short, stable, non-reversible token derived from `passportSeal`.
  // IMPORTANT: We intentionally never render the `passportSeal` text itself.
  const seed = hashStringToUint32(String(input ?? ""));
  return seed.toString(36).toUpperCase().padStart(7, "0").slice(0, 7);
}

function getSecondarySealLabel(passportSealText) {
  const t = String(passportSealText ?? "").toLowerCase();
  if (!t) return "CUSTOMS CLEARANCE";
  if (/(wave|waves|fjord|harbor|harbour|sea|ocean|coast|dhow|caravel)/.test(t)) {
    return "PORT AUTHORITY";
  }
  if (/(eagle|falcon|condor|rooster|bird|parrot|hummingbird|frigate|harpy)/.test(t)) {
    return "AERIAL CLEARANCE";
  }
  if (/(mountain|andes|alpine|peak|ridge|volcano|fuji)/.test(t)) {
    return "HIGHLAND POST";
  }
  if (/(cross|white cross|double cross|crown)/.test(t)) {
    return "CIVIC OFFICE";
  }
  if (/(laurel|olive|branches|wreath|leaf)/.test(t)) {
    return "STATE ARCHIVE";
  }
  if (/(star|stars|southern cross|seven-pointed|five-pointed)/.test(t)) {
    return "STAR OFFICE";
  }
  return "CUSTOMS CLEARANCE";
}

function detectScriptFromText(input) {
  const t = String(input ?? "");
  if (/([\u0600-\u06FF])/.test(t)) return "arabic";
  if (/([\u0370-\u03FF])/.test(t)) return "greek";
  if (/([\u0400-\u04FF])/.test(t)) return "cyrillic";
  if (/([\u0E00-\u0E7F])/.test(t)) return "thai";
  if (/([\u0900-\u097F])/.test(t)) return "devanagari";
  if (/([\uAC00-\uD7AF])/.test(t)) return "korean";
  if (/([\u3040-\u30FF])/.test(t)) return "japanese";
  if (/([\u4E00-\u9FFF])/.test(t)) return "cjk";
  return "latin";
}

function getEntryStampLabel({ country, countryKey, meta }) {
  // Goal: Render the mini "ENTRY" stamp in the country's language.
  // We use a small explicit map for common Latin-script languages and a script-based
  // fallback for non-Latin scripts (derived from the motto when available).
  const key = countryKey ?? normalizeCountryKey(country ?? "");

  const byCountryKey = {
    // Arabic
    unitedarabemirates: "دخول",
    bahrain: "دخول",
    djibouti: "دخول",
    // CJK / East Asia
    china: "入境",
    hongkong: "入境",
    japan: "入国",
    southkorea: "입국",
    koreasouth: "입국",
    // Greek / Cyrillic
    greece: "ΕΙΣΟΔΟΣ",
    bulgaria: "ВХОД",
    // Major Latin-script translations
    france: "ENTRÉE",
    canada: "ENTRÉE",
    belgium: "ENTRÉE",
    spain: "ENTRADA",
    argentina: "ENTRADA",
    chile: "ENTRADA",
    colombia: "ENTRADA",
    costarica: "ENTRADA",
    ecuador: "ENTRADA",
    brazil: "ENTRADA",
    italy: "INGRESSO",
    germany: "EINREISE",
    austria: "EINREISE",
    switzerland: "EINREISE",
    denmark: "INDREJSE",
    // India (Devanagari)
    india: "प्रवेश",
  };

  if (key && byCountryKey[key]) return byCountryKey[key];

  const script = detectScriptFromText(meta?.motto);
  switch (script) {
    case "arabic":
      return "دخول";
    case "greek":
      return "ΕΙΣΟΔΟΣ";
    case "cyrillic":
      return "ВЪЕЗД";
    case "thai":
      return "เข้าเมือง";
    case "devanagari":
      return "प्रवेश";
    case "korean":
      return "입국";
    case "japanese":
      return "入国";
    case "cjk":
      return "入境";
    case "latin":
    default:
      return "ENTRY";
  }
}

function pickDeclarationPhrase(script, rng) {
  const roll = rng();
  switch (script) {
    case "arabic":
      return roll < 0.5 ? "إقرار الدخول" : "تصريح الدخول";
    case "greek":
      return roll < 0.5 ? "ΔΗΛΩΣΗ" : "ΔΗΛΩΣΗ ΕΙΣΟΔΟΥ";
    case "cyrillic":
      return roll < 0.5 ? "ДЕКЛАРАЦИЯ" : "ЗАЯВЛЕНИЕ";
    case "thai":
      return roll < 0.5 ? "คำประกาศ" : "คำรับรอง";
    case "devanagari":
      return roll < 0.5 ? "घोषणा" : "घोषणापत्र";
    case "korean":
      return roll < 0.5 ? "선언" : "신고";
    case "japanese":
      return roll < 0.5 ? "宣言" : "申告";
    case "cjk":
      return roll < 0.5 ? "声明" : "申明";
    case "latin":
    default: {
      // Latin-script variations (not country-perfect, but intentionally varied).
      const options = [
        "DECLARATION",
        "CUSTOMS DECLARATION",
        "ENTRY DECLARATION",
        "ARRIVAL DECLARATION",
        "ATTESTATION",
        "MANIFEST",
        "CLEARANCE NOTE",
        "DÉCLARATION",
        "DECLARACIÓN",
        "DECLARAÇÃO",
        "DICHIARAZIONE",
        "ERKLÄRUNG",
        "VERKLARING",
        "DEKLARATION",
        "DEKLARACJA",
      ];
      return options[Math.floor(rng() * options.length)];
    }
  }
}

function formatEmblemLine(templateIndex, phrase, place, sealToken) {
  switch (templateIndex % 6) {
    case 0:
      return `${phrase} • ${place} • SEAL ${sealToken}`;
    case 1:
      return `SEAL ${sealToken} • ${phrase} • ${place}`;
    case 2:
      return `${phrase}: ${place} — ${sealToken}`;
    case 3:
      return `${phrase} / ${place} / ${sealToken}`;
    case 4:
      return `${place} • ${phrase} • ID ${sealToken}`;
    case 5:
    default:
      return `${phrase} — ${place} — SEAL ${sealToken}`;
  }
}

function getSealMotifKind(passportSealText) {
  const t = String(passportSealText ?? "").toLowerCase();
  if (!t) return "ring";
  if (/(star|stars|southern cross|seven-pointed|five-pointed)/.test(t)) return "star";
  if (/(wave|waves|fjord|harbor|harbour|sea|ocean|coast)/.test(t)) return "waves";
  if (/(cross|white cross|double cross)/.test(t)) return "cross";
  if (/(laurel|olive|branches|wreath)/.test(t)) return "laurel";
  if (/(eagle|falcon|condor|rooster|bird|parrot|hummingbird|frigate)/.test(t)) return "wings";
  if (/(mountain|andes|alpine|peak|ridge|volcano|fuji)/.test(t)) return "mountains";
  return "ring";
}

function SealMotif({ kind }) {
  switch (kind) {
    case "star":
      return (
        <polygon
          points="60,36 65.2,50.6 80.8,50.8 68.2,60.2 72.8,75 60,66.2 47.2,75 51.8,60.2 39.2,50.8 54.8,50.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      );
    case "waves":
      return (
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M30 62 C38 56, 46 56, 54 62 S70 68, 78 62" />
          <path d="M30 72 C38 66, 46 66, 54 72 S70 78, 78 72" opacity="0.85" />
        </g>
      );
    case "cross":
      return (
        <g fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
          <path d="M60 40 V80" />
          <path d="M40 60 H80" />
        </g>
      );
    case "laurel":
      return (
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M44 76 C36 70, 34 58, 42 48" />
          <path d="M76 76 C84 70, 86 58, 78 48" />
          <path d="M44 76 C48 72, 50 68, 50 64" opacity="0.65" />
          <path d="M76 76 C72 72, 70 68, 70 64" opacity="0.65" />
        </g>
      );
    case "wings":
      return (
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
          <path d="M60 56 C52 44, 40 44, 34 52 C40 52, 46 56, 50 62" />
          <path d="M60 56 C68 44, 80 44, 86 52 C80 52, 74 56, 70 62" />
          <path d="M52 64 C56 66, 58 68, 60 72 C62 68, 64 66, 68 64" opacity="0.8" />
        </g>
      );
    case "mountains":
      return (
        <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
          <path d="M34 78 L50 54 L62 70 L70 60 L86 78" />
          <path d="M50 54 L56 62" opacity="0.75" />
          <path d="M70 60 L74 66" opacity="0.75" />
        </g>
      );
    case "ring":
    default:
      return <circle cx="60" cy="62" r="14" fill="none" stroke="currentColor" strokeWidth="2" />;
  }
}

const INK_COLORS = [
  "#17324D", // navy
  "#7A1026", // maroon
  "#1C4A2E", // green
  "#3C2A1E", // sepia
  "#2F2F35", // graphite
  "#0F4C5C", // teal
];

const SIGNATURE_FONTS = [
  '"Segoe Script"',
  '"Lucida Handwriting"',
  '"Brush Script MT"',
  '"Snell Roundhand"',
  '"Bradley Hand"',
  "cursive",
];

// Reference Information (from Assistant.md): use these names/mottos/seals when available.
// If a visa is locked (not yet visited), we deliberately do NOT render signatures or seals.
const VISA_REFERENCE_LIST = [
  {
    country: "United Arab Emirates",
    personName: "Saeed Al Nuaimi",
    motto: "نصنع الغد معًا",
    passportSeal: "Falcon silhouette above a dhow sail and seven-pointed star",
  },
  {
    country: "Antigua and Barbuda",
    personName: "Keisha Edwards",
    motto: "One sea, one home.",
    passportSeal: "Frigate bird with a rising sun over waves",
  },
  {
    country: "Argentina",
    personName: "Lucía Fernández",
    motto: "Con paso firme.",
    passportSeal: "Sun of May with laurel branches",
  },
  {
    country: "Austria",
    personName: "Johann Gruber",
    motto: "Mutig und bedacht.",
    passportSeal: "Eagle with an edelweiss and alpine peak",
  },
  {
    country: "Australia",
    personName: "Mia Thompson",
    motto: "Fair winds ahead.",
    passportSeal: "Kangaroo and emu with the Southern Cross",
  },
  {
    country: "Belgium",
    personName: "Anouk De Smet",
    motto: "Samen sterk.",
    passportSeal: "Heraldic lion with a small crown and laurel",
  },
  {
    country: "Bulgaria",
    personName: "Nikolay Petrov",
    motto: "С чест и труд.",
    passportSeal: "Lion emblem with a Bulgarian rose",
  },
  {
    country: "Bahrain",
    personName: "Fatima Al Khalifa",
    motto: "سلامٌ وبركة.",
    passportSeal: "Pearl above stylized waves and a dhow",
  },
  {
    country: "Brazil",
    personName: "Rafael Silva",
    motto: "Avançar com alegria.",
    passportSeal: "Southern Cross stars with coffee and laurel",
  },
  {
    country: "Belize",
    personName: "Marisol Castillo",
    motto: "Small country, big heart.",
    passportSeal: "Mahogany tree with a reef wave pattern",
  },
  {
    country: "Canada",
    personName: "Élise Tremblay",
    motto: "Unis par le nord.",
    passportSeal: "Maple leaf with a polar star and fir branch",
  },
  {
    country: "Switzerland",
    personName: "Lukas Meier",
    motto: "Frei und verlässlich.",
    passportSeal: "White cross with a mountain outline",
  },
  {
    country: "Chile",
    personName: "Valentina Rojas",
    motto: "Entre mar y cordillera.",
    passportSeal: "Andes ridge with a condor in flight",
  },
  {
    country: "China",
    personName: "Wang Jie",
    motto: "携手前行",
    passportSeal: "Five-star motif with a peony blossom",
  },
  {
    country: "Colombia",
    personName: "Camilo Herrera",
    motto: "Café y montaña.",
    passportSeal: "Coffee branch with an Andean condor",
  },
  {
    country: "Costa Rica",
    personName: "Daniela Mora",
    motto: "Pura vida, siempre.",
    passportSeal: "Volcano cone with a coffee sprig",
  },
  {
    country: "Cyprus",
    personName: "Andreas Georgiou",
    motto: "Ειρήνη στο νησί.",
    passportSeal: "Olive branches around the island outline",
  },
  {
    country: "Czech Republic",
    personName: "Tereza Nováková",
    motto: "Srdcem vpřed.",
    passportSeal: "Bohemian lion with a linden leaf",
  },
  {
    country: "Germany",
    personName: "Hannah Schneider",
    motto: "Gemeinsam voran.",
    passportSeal: "Federal eagle with an oak leaf",
  },
  {
    country: "Djibouti",
    personName: "Hassan Guelleh",
    motto: "وحدةٌ وسلام.",
    passportSeal: "Five-pointed star above coastline and palm",
  },
  {
    country: "Denmark",
    personName: "Freja Nielsen",
    motto: "Ro i sindet.",
    passportSeal: "Three lions with a wave band",
  },
  {
    country: "Dominica",
    personName: "Janelle Laurent",
    motto: "Nature is our pride.",
    passportSeal: "Sisserou parrot with a rainforest leaf",
  },
  {
    country: "Dominican Republic",
    personName: "Luis Miguel Peña",
    motto: "Fe y trabajo.",
    passportSeal: "Palm and laurel around a small cross",
  },
  {
    country: "Ecuador",
    personName: "Sofía Paredes",
    motto: "Del sol a la nieve.",
    passportSeal: "Chimborazo peak with a Galápagos tortoise",
  },
  {
    country: "Estonia",
    personName: "Maarja Tamm",
    motto: "Üheskoos edasi.",
    passportSeal: "Three lions with a cornflower",
  },
  {
    country: "Spain",
    personName: "Álvaro García",
    motto: "Siempre hacia adelante.",
    passportSeal: "Pillars motif with a small crown",
  },
  {
    country: "Finland",
    personName: "Aino Korhonen",
    motto: "Hiljaa ja vahvasti.",
    passportSeal: "Lion with a pine branch and lake wave",
  },
  {
    country: "France",
    personName: "Camille Dubois",
    motto: "Libres et solidaires.",
    passportSeal: "Rooster profile with laurel",
  },
  {
    country: "United Kingdom",
    personName: "Oliver Hughes",
    motto: "Steady as we go.",
    passportSeal: "Crown above a lion shield",
  },
  {
    country: "Grenada",
    personName: "Shanice Joseph",
    motto: "Spice of life.",
    passportSeal: "Nutmeg pod with a sail over waves",
  },
  {
    country: "Guadeloupe",
    personName: "Jean-Baptiste Moreau",
    motto: "Île au grand souffle.",
    passportSeal: "La Soufrière volcano with sugarcane",
  },
  {
    country: "Greece",
    personName: "Eleni Papadopoulou",
    motto: "Φως και θάλασσα.",
    passportSeal: "Owl with an olive branch",
  },
  {
    country: "Guatemala",
    personName: "Ana López",
    motto: "Quetzal en vuelo.",
    passportSeal: "Quetzal bird over a scroll and laurel",
  },
  {
    country: "Guam",
    personName: "Tasi Cruz",
    motto: "Ta fanmåolek.",
    passportSeal: "Latte stone with a coconut palm and wave",
  },
  {
    country: "Hong Kong",
    personName: "Chan Mei-ling",
    motto: "同心向前",
    passportSeal: "Bauhinia flower above a harbor wave",
  },
  {
    country: "Haiti",
    personName: "Jean-Pierre Charles",
    motto: "Men anpil, chay pa lou.",
    passportSeal: "Palm tree with drum and banner",
  },
  {
    country: "Hungary",
    personName: "Bence Kovács",
    motto: "Békében, erőben.",
    passportSeal: "Crown with a double cross",
  },
  {
    country: "Indonesia",
    personName: "Siti Rahmawati",
    motto: "Rukun itu indah.",
    passportSeal: "Garuda with rice and cotton",
  },
  {
    country: "Ireland",
    personName: "Aoife O'Connor",
    motto: "Ní neart go cur le chéile.",
    passportSeal: "Celtic harp with shamrock",
  },
  {
    country: "Israel",
    personName: "Noam Levi",
    motto: "שלום בבית, אור בדרך.",
    passportSeal: "Menorah with olive branches",
  },
  {
    country: "India",
    personName: "Priya Sharma",
    motto: "एकता में बल",
    passportSeal: "Ashoka lions with a lotus",
  },
  {
    country: "Iceland",
    personName: "Einar Jónsson",
    motto: "Hugrekki og heiðarleiki.",
    passportSeal: "Geyser plume under northern lights",
  },
  {
    country: "Italy",
    personName: "Giulia Romano",
    motto: "Cuore e coraggio.",
    passportSeal: "Olive branch with a small star",
  },
  {
    country: "Jamaica",
    personName: "Kemar Brown",
    motto: "Wi likkle but wi tallawah.",
    passportSeal: "Doctor bird hummingbird with ackee",
  },
  {
    country: "Japan",
    personName: "Haruka Sato",
    motto: "一歩ずつ前へ",
    passportSeal: "Chrysanthemum with Mount Fuji outline",
  },
  {
    country: "Saint Kitts and Nevis",
    personName: "Aaliyah Martin",
    motto: "Two islands, one spirit.",
    passportSeal: "Sugarcane stalks with a frigate bird",
  },
  {
    country: "South Korea",
    personName: "Kim Min-jun",
    motto: "함께 더 멀리",
    passportSeal: "Taegeuk symbol with mugunghwa blossom",
  },
  {
    country: "Saint Lucia",
    personName: "Khadija Samuel",
    motto: "Rise with the Pitons.",
    passportSeal: "Twin Pitons peaks with a palm frond",
  },
  {
    country: "Lithuania",
    personName: "Mantas Kazlauskas",
    motto: "Laisvė širdyje.",
    passportSeal: "Vytis rider with an oak leaf",
  },
  {
    country: "Latvia",
    personName: "Laura Bērziņa",
    motto: "Saule un spēks.",
    passportSeal: "Three stars above a sun motif",
  },
  {
    country: "Martinique",
    personName: "Élodie Pierre-Louis",
    motto: "Toujours vers la mer.",
    passportSeal: "Mont Pelée with sugarcane and waves",
  },
  {
    country: "Malta",
    personName: "Matthew Camilleri",
    motto: "Qalbna b'saħħitha.",
    passportSeal: "Maltese cross with an olive twig",
  },
  {
    country: "Mexico",
    personName: "Diego Hernández",
    motto: "Tierra y esperanza.",
    passportSeal: "Eagle with serpent on a cactus",
  },
  {
    country: "Malaysia",
    personName: "Nur Aisyah Ahmad",
    motto: "Bersatu kita maju.",
    passportSeal: "Hibiscus with crescent and star",
  },
  {
    country: "Netherlands",
    personName: "Sanne van Dijk",
    motto: "Samen over het water.",
    passportSeal: "Windmill with tulip and wave",
  },
  {
    country: "Norway",
    personName: "Ingrid Hansen",
    motto: "Sterk i stillhet.",
    passportSeal: "Lion with a fjord line",
  },
  {
    country: "New Zealand",
    personName: "Wiremu Te Rangi",
    motto: "Kia kaha, kia māia.",
    passportSeal: "Silver fern with a kiwi silhouette",
  },
  {
    country: "Panama",
    personName: "María José Batista",
    motto: "Puente del mundo.",
    passportSeal: "Canal lock gates with a harpy eagle",
  },
  {
    country: "Peru",
    personName: "Renato Quispe",
    motto: "Sierra y selva.",
    passportSeal: "Vicuña with Andean mountain outline",
  },
  {
    country: "Philippines",
    personName: "Angelica Santos",
    motto: "Bayanihan ang lakas.",
    passportSeal: "Sun with three stars and sampaguita",
  },
  {
    country: "Poland",
    personName: "Katarzyna Nowak",
    motto: "Z sercem i honorem.",
    passportSeal: "White eagle with wheat",
  },
  {
    country: "Puerto Rico",
    personName: "Isabela Rivera",
    motto: "Isla de valor.",
    passportSeal: "Garita watchtower with a coquí frog",
  },
  {
    country: "Portugal",
    personName: "Tiago Pereira",
    motto: "Mar e destino.",
    passportSeal: "Armillary sphere with a small caravel",
  },
  {
    country: "Qatar",
    personName: "Mohammed Al Mansoori",
    motto: "العزم يفتح الطريق.",
    passportSeal: "Dhow with palm and desert dune",
  },
  {
    country: "Romania",
    personName: "Andrei Popescu",
    motto: "Cu rădăcini și aripi.",
    passportSeal: "Eagle with Carpathian ridge",
  },
  {
    country: "Sweden",
    personName: "Elin Johansson",
    motto: "Lagom är bäst.",
    passportSeal: "Three crowns with a pine sprig",
  },
  {
    country: "Singapore",
    personName: "Wei Ling Tan",
    motto: "Majulah bersama.",
    passportSeal: "Merlion with an orchid",
  },
  {
    country: "Slovenia",
    personName: "Nika Kovač",
    motto: "Gore nas vodijo.",
    passportSeal: "Triglav mountain with a linden leaf",
  },
  {
    country: "El Salvador",
    personName: "Carlos Mejía",
    motto: "Volcanes y valor.",
    passportSeal: "Volcano cone with laurel wreath",
  },
  {
    country: "Sint Maarten",
    personName: "Jordy van Gendt",
    motto: "One island, many stories.",
    passportSeal: "Pelican over a courthouse arch and waves",
  },
  {
    country: "Thailand",
    personName: "Sirin Kittisak",
    motto: "ก้าวไปด้วยกัน",
    passportSeal: "Garuda with a lotus",
  },
  {
    country: "Tunisia",
    personName: "Amira Ben Youssef",
    motto: "حرية وكرامة.",
    passportSeal: "Crescent and star with a jasmine blossom",
  },
  {
    country: "Türkiye",
    personName: "Emre Yılmaz",
    motto: "Birlikte daha güçlü.",
    passportSeal: "Star and crescent with a tulip motif",
  },
  {
    country: "Trinidad and Tobago",
    personName: "Kaya Mohammed",
    motto: "Rhythm in our roots.",
    passportSeal: "Steelpan drum with a hummingbird",
  },
  {
    country: "Taiwan",
    personName: "Lin Yu-ting",
    motto: "平安同行",
    passportSeal: "Plum blossom with the island outline",
  },
  {
    country: "United States of America",
    personName: "Jordan Miller",
    motto: "Home of the Free",
    passportSeal: "Bald eagle with stars and olive branch",
  },
  {
    country: "Saint Vincent and the Grenadines",
    personName: "Shamara King",
    motto: "Sea breeze, strong roots.",
    passportSeal: "Saint Vincent parrot over waves",
  },
  {
    country: "Virgin Islands (British)",
    personName: "Rochelle Smith",
    motto: "Light the way.",
    passportSeal: "Cluster of oil lamps with a small ship",
  },
  {
    country: "Virgin Islands (U.S.)",
    personName: "Darius Thomas",
    motto: "Islands in harmony.",
    passportSeal: "Eagle with laurel and yellow cedar sprig",
  },
  {
    country: "Vietnam",
    personName: "Nguyễn Thị Lan",
    motto: "Bình yên và chăm chỉ.",
    passportSeal: "Golden star with a lotus",
  },
  {
    country: "South Africa",
    personName: "Thabo Nkosi",
    motto: "Umuntu ngumuntu ngabantu.",
    passportSeal: "Protea flower with a rising sun",
  },
];

const VISA_REFERENCE_BY_COUNTRY = VISA_REFERENCE_LIST.reduce((acc, entry) => {
  acc[entry.country] = entry;
  return acc;
}, {});

function normalizeCountryKey(value) {
  if (!value) return "";

  // Normalize diacritics + punctuation for resilient lookups.
  const base = String(value)
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  // Common textual variants.
  const aliased = base
    .replace(/&/g, " and ")
    .replace(/\busa\b/g, "united states of america")
    .replace(/\bantigua and barbados\b/g, "antigua and barbuda")
    .replace(/\bturkey\b/g, "turkiye");

  return aliased
    .replace(/[^a-z0-9\s()'.-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const VISA_REFERENCE_BY_KEY = VISA_REFERENCE_LIST.reduce((acc, entry) => {
  acc[normalizeCountryKey(entry.country)] = entry;
  return acc;
}, {});

const FALLBACK_FIRST_NAMES = [
  "Mia",
  "Freja",
  "Lucía",
  "Aino",
  "Noam",
  "Priya",
  "Einar",
  "Giulia",
  "Kemar",
  "Valentina",
  "Wei",
  "Nika",
  "Renato",
  "Aoife",
  "Sofía",
  "Bence",
  "Anouk",
  "Daniela",
];

const FALLBACK_LAST_NAMES = [
  "Silva",
  "Novák",
  "Hernández",
  "Kim",
  "Singh",
  "Dubois",
  "Schneider",
  "Rojas",
  "Kovač",
  "Meier",
  "Nielsen",
  "Castillo",
  "Rahmawati",
  "O'Connor",
  "Tan",
  "Popescu",
  "Petrov",
  "Mora",
];

function buildFallbackMeta(country, rng) {
  const first = FALLBACK_FIRST_NAMES[Math.floor(rng() * FALLBACK_FIRST_NAMES.length)];
  const last = FALLBACK_LAST_NAMES[Math.floor(rng() * FALLBACK_LAST_NAMES.length)];

  // A deterministic but unobtrusive uniqueness guard.
  const suffix = Math.floor(rng() * 36)
    .toString(36)
    .toUpperCase();

  return {
    // Ensure uniqueness across countries even when name pools collide.
    personName: `${first} ${last}-${suffix}`,
    motto: "Clear skies ahead.",
    passportSeal: `FDXVA Entry Seal ${suffix}`,
  };
}

export default function Visa({ visa }) {
  // Avoid React 18-only hooks (useId). Keep IDs stable per instance.
  const instanceIdRef = useRef(null);
  if (!instanceIdRef.current) {
    instanceIdRef.current = Math.random().toString(36).slice(2);
  }

  const model = visa ?? {};
  const country = model.country ?? "Unknown Port";
  const countryKey = normalizeCountryKey(country);
  const flagCode = model.flag;
  const portOfEntry = model.icao;
  const epochSeconds = Number(model.epoch);
  const isUnlocked = Number.isFinite(epochSeconds) && epochSeconds > 0;

  const seed = useMemo(
    () => hashStringToUint32(`${country}|${flagCode ?? ""}`),
    [country, flagCode]
  );

  const noiseId = useMemo(
    () => `visaNoise_${seed}_${instanceIdRef.current}`,
    [seed]
  );

  const sealPathId = useMemo(
    () => `visaSealPath_${seed}_${instanceIdRef.current}`,
    [seed]
  );

  const secondarySealPatternId = useMemo(
    () => `visaSealPattern2_${seed}_${instanceIdRef.current}`,
    [seed]
  );

  const computed = useMemo(() => {
    const rng = mulberry32(seed);

    const tiltDeg = 3 + rng() * 6; // 3–9
    const sealTiltDeg = -9 + rng() * 18;
    const liftTiltDeg = -2 + rng() * 4;

    const ink = INK_COLORS[Math.floor(rng() * INK_COLORS.length)];
    const signatureFont = SIGNATURE_FONTS[Math.floor(rng() * SIGNATURE_FONTS.length)];

    // Wear: higher = more faded/uneven.
    const wear = clamp(rng(), 0, 1);
    const inkBleedPx = 0.15 + wear * 0.55;
    const stampOpacity = 0.78 - wear * 0.28;

    const referenceMeta =
      VISA_REFERENCE_BY_COUNTRY[country] ?? VISA_REFERENCE_BY_KEY[countryKey];
    const meta = referenceMeta ?? buildFallbackMeta(country, rng);

    // Never render `passportSeal` text. Use it only to drive visuals.
    const passportSealText = String(meta.passportSeal ?? "");
    const sealMotifKind = getSealMotifKind(passportSealText);
    const sealToken = makeSealToken(passportSealText || country);
    const secondarySealLabel = getSecondarySealLabel(passportSealText);

    const declarationPhrase = pickDeclarationPhrase(detectScriptFromText(meta.motto), rng);
    const emblemTemplateIndex = Math.floor(rng() * 6);

    const entryLabel = getEntryStampLabel({ country, countryKey, meta });

    const miniStamp = {
      radiusPx: Math.round(4 + rng() * 28),
      innerRadiusPx: 0,
      borderWidthPx: Math.round(1 + rng() * 3),
      borderStyle: ["solid", "dashed", "double"][Math.floor(rng() * 3)],
      insetBorderWidthPx: rng() < 0.55 ? 0 : Math.round(1 + rng() * 2),
      insetBorderStyle: rng() < 0.7 ? "solid" : "dashed",
      outlineWidthPx: rng() < 0.45 ? 0 : Math.round(1 + rng() * 2),
      outlineStyle: ["solid", "dashed", "dotted"][Math.floor(rng() * 3)],
      outlineOffsetPx: Math.round(1 + rng() * 2),
      padX: 8 + Math.round(rng() * 8),
      padY: 4 + Math.round(rng() * 4),
      tiltDeg: -12 + rng() * 22,
      bgAlpha: 0.05 + rng() * 0.22,
      shadowAlpha: 0.05 + rng() * 0.16,
      shadowYpx: Math.round(1 + rng() * 2),
      shadowBlurPx: Math.round(1 + rng() * 2),
    };

    miniStamp.innerRadiusPx = Math.max(0, miniStamp.radiusPx - 6);

    const secondarySeal = {
      rotateDeg: -18 + rng() * 36,
      scale: 0.76 + rng() * 0.18,
      // Keep the secondary seal fainter than the primary.
      opacityFactor: 0.26 + (1 - wear) * 0.06,
    };

    return {
      tiltDeg,
      sealTiltDeg,
      liftTiltDeg,
      ink,
      signatureFont,
      wear,
      inkBleedPx,
      stampOpacity,
      meta,
      sealMotifKind,
      sealToken,
      secondarySealLabel,
      secondarySeal,
      declarationPhrase,
      emblemTemplateIndex,
      entryLabel,
      miniStamp,
    };
  }, [country, countryKey, seed]);

  const flightDate = isUnlocked ? formatDate(epochSeconds) : null;
  const arrivalTimeUtc = isUnlocked ? formatUtcTime(epochSeconds) : null;

  const title = isUnlocked
    ? `Arrival confirmed ${flightDate} at ${arrivalTimeUtc} UTC. Port of Entry: ${portOfEntry ?? country}.`
    : `Visa pending. Port of Entry: ${portOfEntry ?? country}.`;

  const placeLabel = portOfEntry ?? country;
  const emblemLine = isUnlocked
    ? formatEmblemLine(
        computed.emblemTemplateIndex,
        computed.declarationPhrase,
        placeLabel,
        computed.sealToken
      )
    : null;

  const cardStyle = {
    "--visa-ink": computed.ink,
    // Always end with a generic cursive fallback.
    "--visa-sig-font": `${computed.signatureFont}, cursive`,
    "--visa-seal-tilt": `${computed.sealTiltDeg}deg`,
    "--visa-lift-tilt": `${computed.liftTiltDeg}deg`,
    "--visa-bleed": `${computed.inkBleedPx}px`,
    "--visa-stamp-opacity": computed.stampOpacity,
    "--visa-mini-radius": `${computed.miniStamp.radiusPx}px`,
    "--visa-mini-inner-radius": `${computed.miniStamp.innerRadiusPx}px`,
    "--visa-mini-border-width": `${computed.miniStamp.borderWidthPx}px`,
    "--visa-mini-border-style": computed.miniStamp.borderStyle,
    "--visa-mini-inset-border-width": `${computed.miniStamp.insetBorderWidthPx}px`,
    "--visa-mini-inset-border-style": computed.miniStamp.insetBorderStyle,
    "--visa-mini-outline-width": `${computed.miniStamp.outlineWidthPx}px`,
    "--visa-mini-outline-style": computed.miniStamp.outlineStyle,
    "--visa-mini-outline-offset": `${computed.miniStamp.outlineOffsetPx}px`,
    "--visa-mini-pad-x": `${computed.miniStamp.padX}px`,
    "--visa-mini-pad-y": `${computed.miniStamp.padY}px`,
    "--visa-mini-tilt": `${computed.miniStamp.tiltDeg}deg`,
    "--visa-mini-bg-alpha": String(computed.miniStamp.bgAlpha),
    "--visa-mini-shadow-alpha": String(computed.miniStamp.shadowAlpha),
    "--visa-mini-shadow-y": `${computed.miniStamp.shadowYpx}px`,
    "--visa-mini-shadow-blur": `${computed.miniStamp.shadowBlurPx}px`,
  };

  
  return (
    <article
      className={`visa ${isUnlocked ? "visa--unlocked" : "visa--locked"}`}
      style={cardStyle}
      title={title}
    >
      <header className="visa__header">
        <h2 className="visa__country">{country}</h2>
        <div className="visa__arrival">
          {isUnlocked ? (
            <>
              <div className="visa__arrivalLine">
                Arrival confirmed {flightDate} at {arrivalTimeUtc} UTC.
              </div>
              <div className="visa__arrivalLine">Port of Entry: {portOfEntry ?? country}.</div>
            </>
          ) : (
            <>
              <div className="visa__arrivalLine">Your logbook is waiting, Captain.</div>
              <div className="visa__arrivalLine">Port of Entry: {portOfEntry ?? country}.</div>
            </>
          )}
        </div>
      </header>

      <section className="visa__stampArea" aria-label={`${country} visa stamp`}>
        <div className="visa__paper">
          {flagCode ? (
            <img
              className="visa__flag"
              // src={`/flags/${flagCode}.svg`}
              src={`${process.env.PUBLIC_URL}/flags/${flagCode}.svg`}
              alt={`${country} flag`}
              loading="lazy"
            />
          ) : (
            <div className="visa__flagPlaceholder" aria-hidden="true" />
          )}

          {/* Grain overlay (SVG turbulence) */}
          <svg className="visa__grain" aria-hidden="true" focusable="false">
            <filter id={noiseId}>
              <feTurbulence
                type="fractalNoise"
                baseFrequency="0.65"
                numOctaves="3"
                stitchTiles="stitch"
              />
              <feColorMatrix type="saturate" values="0" />
            </filter>
            <rect width="100%" height="100%" filter={`url(#${noiseId})`} opacity="0.25" />
          </svg>

          {/* Circular seal (only when unlocked) */}
          {isUnlocked && (
            <svg
              className="visa__seal visa__seal--primary"
              viewBox="0 0 120 120"
              aria-hidden="true"
              focusable="false"
            >
              <title>{`${country} entry seal`}</title>
              <defs>
                <path
                  id={sealPathId}
                  d="M 60,60 m -46,0 a 46,46 0 1,1 92,0 a 46,46 0 1,1 -92,0"
                />
              </defs>

              <circle cx="60" cy="60" r="46" fill="none" stroke="currentColor" strokeWidth="3" />
              <circle cx="60" cy="60" r="36" fill="none" stroke="currentColor" strokeWidth="1" />

              <text
                className="visa__sealRingText"
                fontSize="9"
                fontFamily="ui-monospace, Consolas, 'Courier New', monospace"
              >
                <textPath href={`#${sealPathId}`} startOffset="50%" textAnchor="middle">
                  {country.toUpperCase()}
                </textPath>
              </text>

              <g className="visa__sealMotif" aria-hidden="true">
                <SealMotif kind={computed.sealMotifKind} />
              </g>

              <text
                x="60"
                y="52"
                textAnchor="middle"
                fontSize="12"
                fontFamily="ui-monospace, Consolas, 'Courier New', monospace"
              >
                FDXVA
              </text>
              <text
                x="60"
                y="92"
                textAnchor="middle"
                fontSize="7"
                fontFamily="ui-monospace, Consolas, 'Courier New', monospace"
              >
                {`SEAL ${computed.sealToken}`}
              </text>
            </svg>
          )}

          {/* Additional small seal derived from (but not displaying) passportSeal text */}
          {isUnlocked && (
            <svg
              className="visa__seal visa__seal--secondary"
              viewBox="0 0 120 120"
              aria-hidden="true"
              focusable="false"
              style={{
                top: "auto",
                right: "12px",
                bottom: "10px",
                left: "auto",
                width: "74px",
                height: "74px",
                transform: `rotate(${computed.secondarySeal.rotateDeg}deg) scale(${computed.secondarySeal.scale})`,
                opacity: `calc(var(--visa-stamp-opacity, 0.75) * ${computed.secondarySeal.opacityFactor})`,
              }}
            >
              <title>{`${country} clearance seal`}</title>
              <defs>
                <pattern
                  id={secondarySealPatternId}
                  width="6"
                  height="6"
                  patternUnits="userSpaceOnUse"
                  patternTransform="rotate(28)"
                >
                  <line x1="0" y1="0" x2="0" y2="6" stroke="currentColor" strokeWidth="1" opacity="0.35" />
                </pattern>
              </defs>

              {/* Box stamp (very different silhouette than the circular primary seal) */}
              <rect
                x="12"
                y="20"
                width="96"
                height="88"
                rx="10"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinejoin="round"
              />
              <rect
                x="18"
                y="26"
                width="84"
                height="76"
                rx="8"
                fill={`url(#${secondarySealPatternId})`}
                opacity="0.5"
              />
              <rect
                x="18"
                y="26"
                width="84"
                height="76"
                rx="8"
                fill="none"
                stroke="currentColor"
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.9"
              />

              <text
                x="60"
                y="44"
                textAnchor="middle"
                fontSize="9"
                fontFamily="ui-monospace, Consolas, 'Courier New', monospace"
                letterSpacing="1.2"
              >
                {computed.secondarySealLabel}
              </text>

              <g className="visa__sealMotif" aria-hidden="true" opacity="0.9" transform="translate(0, 8)">
                <SealMotif kind={computed.sealMotifKind} />
              </g>

              <text
                x="60"
                y="94"
                textAnchor="middle"
                fontSize="8"
                fontFamily="ui-monospace, Consolas, 'Courier New', monospace"
              >
                {`ID ${computed.sealToken}`}
              </text>
            </svg>
          )}

          {/* Date/typewriter overlay */}
          {isUnlocked && (
            <div className="visa__date" style={{ transform: `rotate(${computed.tiltDeg}deg)` }}>
              {flightDate}
            </div>
          )}

          {/* Mini stamp (only when unlocked) */}
          {isUnlocked && (
            <div className="visa__miniStamp" aria-hidden="true">
              {computed.entryLabel}
            </div>
          )}
        </div>
      </section>

      <footer className="visa__footer">
        {isUnlocked ? (
          <>
            <div className="visa__motto">{computed.meta.motto}</div>
            <div className="visa__emblem">{emblemLine}</div>
            <div className="visa__signatureBlock" aria-label="Customs officer signature">
              <div className="visa__signature">{computed.meta.personName}</div>
              <div className="visa__signatureLine" aria-hidden="true" />
              <div className="visa__signatureLabel">Authorized Signature</div>
            </div>
          </>
        ) : (
          <>
            <div className="visa__motto">Visa not yet validated.</div>
            <div className="visa__emblem">Entry stamp and officer signature will appear after first arrival.</div>
          </>
        )}

        <div className="visa__watermark">© FDXVA — Global Visa Stamp</div>
      </footer>
    </article>
  );
}
